import { useEffect, useRef } from 'react';
import { SESSION_LIVENESS_PROBE_THROTTLE_MS } from '../../config/sessionPolicy';
import { checkSessionLivenessOnResume } from '../../utils/auth/sessionLiveness';

/**
 * Role-agnostic resume liveness probe (2026-07-27 P0-2 security-review fix).
 * Extracted from AuthContext (500-line cap) -- verbatim move of the
 * previous inline effect, no behavior change.
 *
 * A revoked-but-not-yet-expired session (JWT signature still valid, dead
 * auth.sessions row) is invisible to PostgREST/Realtime/Storage -- only a
 * GoTrue-validated getUser() call catches it. This USED to piggyback on
 * ensureFreshSession(), whose only automatic-resume call site
 * (CompanySettingsContext) is gated on `tenantId` -- so it silently never
 * ran for platform super-admins, who have no tenant. This hook is the
 * replacement seam: gated on `sessionUserId` ONLY, so every role (including
 * platform) gets the probe on tab focus/visibility resume. Throttled the
 * same ~60s as before to keep rapid tab-switches cheap.
 */
export function useResumeLivenessProbe(sessionUserId) {
  const lastLivenessProbeRef = useRef(0);
  useEffect(() => {
    if (!sessionUserId) return undefined;
    const maybeProbe = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastLivenessProbeRef.current < SESSION_LIVENESS_PROBE_THROTTLE_MS) return;
      lastLivenessProbeRef.current = now;
      void checkSessionLivenessOnResume();
    };
    document.addEventListener('visibilitychange', maybeProbe);
    window.addEventListener('focus', maybeProbe);
    return () => {
      document.removeEventListener('visibilitychange', maybeProbe);
      window.removeEventListener('focus', maybeProbe);
    };
  }, [sessionUserId]);
}
